import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import type { ReportPreviewPayload } from 'api/analytics-reports/types';
import ViewReportPreviewContent from 'app/modules/analytics/modules/reports/components/ViewAnalyticsReportModal/ViewReportPreviewContent';
import {
  ReportPreviewRenderReadyProvider,
  ReportPreviewRenderReadySignal,
} from 'app/modules/analytics/modules/reports/context/ReportPreviewRenderReadyContext';
import { countPreviewPagesForProfile } from 'app/modules/analytics/modules/reports/utils/countPreviewPagesForProfile';
import AnalyticsReportCoverPage from './AnalyticsReportCoverPage';

export type AnalyticsReportPublicMode = 'minimal' | 'preview' | 'full';

interface Props {
  preview: ReportPreviewPayload;
  mode?: AnalyticsReportPublicMode | null;
}

const AnalyticsReportPublicPage = ({ preview, mode }: Props) => {
  const { t } = useTranslation();
  const showPreviewWrapper = mode === 'preview';

  const totalContentPages = useMemo(
    () =>
      preview.profiles.reduce(
        (sum, profile) => sum + countPreviewPagesForProfile(profile, t),
        0,
      ),
    [preview.profiles, t],
  );

  const totalPages = totalContentPages + 1;

  return (
    <ReportPreviewRenderReadyProvider resetKey={preview.report.id}>
      <div
        className={clsx(
          'analytics-report-pdf-export tw-h-full tw-w-full',
          showPreviewWrapper && 'tw-bg-grayscale-10 tw-px-16 tw-py-8',
        )}
      >
        <ReportPreviewRenderReadySignal />
        <div
          className={clsx(
            'tw-w-full',
            showPreviewWrapper && 'tw-flex tw-flex-col tw-gap-8',
          )}
        >
          <AnalyticsReportCoverPage
            preview={preview}
            layout="export"
            totalPages={totalPages}
          />
          <ViewReportPreviewContent
            preview={preview}
            layout="export"
            breakBeforeFirstPage
            pageOffset={1}
          />
        </div>
      </div>
    </ReportPreviewRenderReadyProvider>
  );
};

export default AnalyticsReportPublicPage;
